import React, { useState } from 'react';
import { Select, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import { routes, subRoutes } from '../../utils/constants/routes';
import {
  contractsStatuses,
  contractsTypes,
  tasksStatuses,
  ticketsPrioritiesColor
} from '../../utils/constants/tagColors';

const { Option, OptGroup } = Select;

const MagicSearchBar = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const { message } = useErrorMessage();
  const [results, setResults] = useState({});
  let timeout;

  const getResults = async (value) => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/magic-search/${value}`
      });
      setResults(data);
    } catch (e) {
      message(e);
    }
    setIsLoading(false);
  };

  const handleSearch = (value) => {
    if (value.length > 5) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      timeout = setTimeout(() => getResults(value), 700);
    } else setResults({});
  };

  const getInfoGivenModel = {
    Customer: ({ first_name, last_name }) => `${first_name} ${last_name}`,
    // eslint-disable-next-line react/prop-types
    Contract: ({ number, status }) => (
      <>
        {`${number} - `}
        <Tag color={contractsStatuses[status]}>
          {t(`contracts.tags.${status}`)}
        </Tag>
      </>
    ),
    // eslint-disable-next-line react/prop-types
    Quote: ({ quote_number, type }) => (
      <>
        {`${quote_number} - `}
        <Tag color={contractsTypes[type]}>{t(`contracts.tags.${type}`)}</Tag>
      </>
    ),
    // eslint-disable-next-line react/prop-types
    Ticket: ({ contract_number, status, priority }) => (
      <>
        {`${contract_number} - `}
        <Tag color={tasksStatuses[status]}>{t(`tickets.tags.${status}`)}</Tag>
        {` - `}
        <Tag color={ticketsPrioritiesColor[priority]}>
          {t(`tickets.tags.${priority}`)}
        </Tag>
      </>
    )
  };

  const handleSelect = ([model, id]) => {
    switch (model) {
      case 'Customer':
        navigate(
          `${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_CLIENTS}/show/${id}`
        );
        break;
      case 'Contract':
        navigate(
          `${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_CONTRACTS}/show/${id}`
        );
        break;
      case 'Quote':
        navigate(`${routes.QUOTES}${subRoutes.QUOTES.QUOTES_SAVED}/show/${id}`);
        break;
      case 'Ticket':
        navigate(
          `${routes.QUOTES}${subRoutes.QUOTES.QUOTES_IN_EXEMPTION}/show/${id}`
        );
        break;
      default:
        break;
    }
  };

  return (
    <Select
      loading={isLoading}
      style={{ width: '100%', margin: '0 30px 24px 35px' }}
      placeholder={
        <>
          <SearchOutlined />
          <span style={{ marginLeft: 10 }}>
            Rechercher par contrat, devis, client, dérogation...
          </span>
        </>
      }
      showSearch
      defaultActiveFirstOption={false}
      notFoundContent={null}
      showArrow={false}
      filterOption={false}
      onSearch={(value) => handleSearch(value)}
      onSelect={handleSelect}
    >
      {Object.entries(results).map(([key, matches]) => (
        <OptGroup label={t(`magic_search.group.title.${key}`)} key={key}>
          {matches.map((m) => (
            <Option
              key={m._id}
              value={[key, key === 'Ticket' ? m.contract : m._id]}
            >
              {getInfoGivenModel[key](m)}
            </Option>
          ))}
        </OptGroup>
      ))}
    </Select>
  );
};

export { MagicSearchBar };
